import React from 'react';
import GlobalVoiceAssistantUI from './GlobalVoiceAssistantUI';
import GlobalVoiceInputHandler from './GlobalVoiceInputHandler';
import VoiceAssistantRail from './VoiceAssistantRail';
import './gva-rail.css';
import Icon from './ui/Icon';
import { useAssistantViewport } from '../hooks/useAssistantViewport';
import type { AssistantDisplayMode } from '../utils/assistantDisplayMode';
import type { AssistantSuggestedQueryPill } from '../constants/assistantSuggestedQueries';
import type { ComponentProps } from 'react';

const RAIL_PANEL_ID = 'gva-rail-panel';

type VoiceAssistantUIProps = ComponentProps<typeof GlobalVoiceAssistantUI>;

export interface VoiceAssistantShellProps extends VoiceAssistantUIProps {
  displayMode: AssistantDisplayMode;
  onCollapseRail: () => void;
  onOpenAssistant: () => void;
  pageSuggestedQueries?: AssistantSuggestedQueryPill[];
}

/**
 * Picks assistant presentation: desktop rail, mobile overlay, or FAB-only trigger.
 */
const VoiceAssistantShell: React.FC<VoiceAssistantShellProps> = ({
  displayMode,
  onCollapseRail,
  onOpenAssistant,
  pageSuggestedQueries,
  isRecording = false,
  onTranscription,
  onProcessingChange,
  ...voiceProps
}) => {
  const isDesktop = useAssistantViewport();

  return (
    <>
      {displayMode === 'rail' && (
        <VoiceAssistantRail
          {...voiceProps}
          isRecording={isRecording}
          pageSuggestedQueries={pageSuggestedQueries}
          onCollapse={onCollapseRail}
          onTranscription={onTranscription}
          onProcessingChange={onProcessingChange}
        />
      )}

      {displayMode === 'overlay' && (
        <GlobalVoiceAssistantUI
          {...voiceProps}
          isRecording={isRecording}
          pageSuggestedQueries={pageSuggestedQueries}
          onTranscription={onTranscription}
          onProcessingChange={onProcessingChange}
        />
      )}

      {displayMode === 'fab' && isDesktop && (
        <button
          type="button"
          className="gva-desktop-fab"
          onClick={onOpenAssistant}
          title="Open voice assistant"
          aria-label="Open voice assistant"
          aria-expanded={false}
          aria-controls={RAIL_PANEL_ID}
        >
          <Icon name={isRecording ? 'mic-stop' : 'mic'} size={22} />
        </button>
      )}

      {/* Always mounted so mobile FAB mic works before the overlay opens */}
      <GlobalVoiceInputHandler
        onTranscription={onTranscription}
        onProcessingChange={onProcessingChange}
      />
    </>
  );
};

export default VoiceAssistantShell;
